import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { useUserGroup, useUpdateUserGroup, type UserGroupUpdate } from "@/hooks/use-user-groups";
import { UserGroupForm } from "@/components/user-groups/user-group-form";
import { PageHeader, Breadcrumbs, PageSkeleton, Card } from "@/components/ui";

export const Route = createFileRoute("/_authenticated/org/settings/groups/$groupId/edit")({
  component: UserGroupEditPage,
});

function UserGroupEditPage() {
  const { t } = useTranslation(["userGroups", "common"]);
  const navigate = useNavigate();
  const { groupId } = Route.useParams();
  const { data: group, isLoading } = useUserGroup(groupId);
  const updateMutation = useUpdateUserGroup(groupId);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  if (isLoading || !group) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <PageSkeleton />
      </div>
    );
  }

  // Route guard: the Administrators group (is_system_admin) is not editable —
  // the server 409s (user_group_not_editable_system_admin). Show a banner + back
  // link and never mount the form (mirrors the detail chrome's Edit suppression
  // and the backflow-device terminal-status route guard).
  if (group.is_system_admin) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <Breadcrumbs
          items={[
            {
              label: t("settings.tabs.groups", { ns: "org" }),
              to: "/org/settings",
              search: { tab: "groups" },
            },
            { label: group.name, to: "/org/settings/groups/$groupId", params: { groupId } },
          ]}
        />
        <Card>
          <p className="text-sm text-on-surface">{t("errors.notEditableSystemAdmin")}</p>
          <Link
            to="/org/settings/groups/$groupId"
            params={{ groupId }}
            className="mt-3 inline-block text-sm text-primary hover:underline"
          >
            {t("common:back")}
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-8">
      <Breadcrumbs
        items={[
          {
            label: t("settings.tabs.groups", { ns: "org" }),
            to: "/org/settings",
            search: { tab: "groups" },
          },
          { label: group.name, to: "/org/settings/groups/$groupId", params: { groupId } },
          { label: t("form.editTitle") },
        ]}
      />
      <PageHeader title={t("form.editTitle")} subtitle={group.name} />
      <div className="mt-6">
        <UserGroupForm
          mode="edit"
          initial={group}
          isSubmitting={updateMutation.isPending}
          submitError={submitError}
          onCancel={() =>
            void navigate({ to: "/org/settings/groups/$groupId", params: { groupId } })
          }
          onSubmit={(body) => {
            setSubmitError(undefined);
            // UserGroupForm's onSubmit emits the shared create|update body union;
            // mode="edit" pins it to the update shape, so the cast is sound.
            updateMutation.mutate(body as UserGroupUpdate, {
              onSuccess: () =>
                void navigate({
                  to: "/org/settings/groups/$groupId",
                  params: { groupId },
                }),
              onError: (err) => {
                // The PATCH 409 is the discriminated UserGroupConflictError —
                // its `code` selects name-conflict vs. system-admin-not-editable.
                const code = err instanceof ApiError ? err.code : undefined;
                setSubmitError(
                  code === "user_group_name_conflict"
                    ? t("errors.nameConflict")
                    : code === "user_group_not_editable_system_admin"
                      ? t("errors.notEditableSystemAdmin")
                      : t("errors.updateFailed"),
                );
              },
            });
          }}
        />
      </div>
    </div>
  );
}
